import { useState } from 'react';
import { Search, Bike, Car, AlertCircle } from 'lucide-react';
import ExpressDetailModal from './ExpressDetailModal';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  en_recojo: 'bg-orange-100 text-orange-800',
  encamino: 'bg-blue-100 text-blue-800',
  entregado: 'bg-green-100 text-green-800',
  rechazado: 'bg-red-100 text-red-800',
  cancelado: 'bg-gray-100 text-gray-600',
  postergado: 'bg-purple-100 text-purple-800',
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  en_recojo: 'En recojo',
  encamino: 'En camino',
  entregado: 'Entregado',
  rechazado: 'Rechazado',
  cancelado: 'Cancelado',
  postergado: 'Postergado',
};

export default function ExpressListTab({
  user_id,
  deliveries,
  drivers,
  updateDelivery,
  selectedDate,
  loading,
}) {
  const [selectedId, setSelectedId] = useState(null);

  // ── Client-side filters (data already scoped to selectedDate by dashboard) ──
  const [statusFilter, setStatusFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [unassigned, setUnassigned] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = deliveries.filter((d) => {
    if (statusFilter && d.status !== statusFilter) return false;
    if (vehicleFilter && d.vehicle_type !== vehicleFilter) return false;
    if (unassigned && (d.driver || d.status !== 'pending')) return false;

    if (search) {
      const q = search.toLowerCase().trim();

      const matchesSearch =
        d.id?.toString().includes(q) ||
        d.user_id?.toString().includes(q) ||
        d.user_username?.toLowerCase().includes(q) ||
        d.recipient_name?.toLowerCase().includes(q) ||
        d.pickup_address?.toLowerCase().includes(q) ||
        d.dropoff_address?.toLowerCase().includes(q);

      if (!matchesSearch) return false;
    }

    return true;
  });

  const unassignedCount = deliveries.filter(
    (d) => !d.driver && d.status === 'pending'
  ).length;

  return (
    <div className="space-y-4">
      {/* Unassigned alert */}
      {unassignedCount > 0 && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          <span>
            <strong>{unassignedCount}</strong> entrega{unassignedCount > 1 ? 's' : ''} sin conductor
          </span>
        </div>
      )}

      {/* Client-side filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Search */}
          <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <label className="text-xs text-gray-400">Buscar</label>
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="ID, user_id, username, destinatario, dirección..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              />
            </div>
          </div>

          {/* Status */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white"
            >
              <option value="">Todos</option>
              {Object.entries(STATUS_LABELS).map(([val, lbl]) => (
                <option key={val} value={val}>
                  {lbl}
                </option>
              ))}
            </select>
          </div>

          {/* Vehicle */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">Vehículo</label>
            <select
              value={vehicleFilter}
              onChange={(e) => setVehicleFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white"
            >
              <option value="">Todos</option>
              <option value="bike">🚴 Moto/Bici</option>
              <option value="car">🚗 Auto</option>
            </select>
          </div>

          {/* Unassigned toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none pb-1.5">
            <input
              type="checkbox"
              checked={unassigned}
              onChange={(e) => setUnassigned(e.target.checked)}
              className="accent-brand-primary"
            />
            Solo sin conductor
          </label>
        </div>
      </div>

      {/* Summary row */}
      <div className="flex gap-3 text-xs text-gray-500">
        <span>{filtered.length} resultado{filtered.length !== 1 ? 's' : ''}</span>
        <span>·</span>
        <span className="text-brand-green font-medium">
          S/ {filtered.reduce((s, d) => s + Number(d.total_price || 0), 0).toFixed(2)} total
        </span>
        <span>·</span>
        <span className="text-brand-primary font-medium">
          S/ {filtered.reduce((s, d) => s + Number(d.fastmax_earnings || 0), 0).toFixed(2)} Fast Max
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm animate-pulse">
            Cargando entregas...
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">
            No hay entregas para esta fecha
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">Usuario</th>
                  <th className="px-4 py-3 text-left">Destinatario</th>
                  <th className="px-4 py-3 text-left">Recojo → Entrega</th>
                  <th className="px-4 py-3 text-left">Vehículo</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-left">Conductor</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">Fast Max</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => setSelectedId(d.id)}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                      !d.driver && d.status === 'pending' ? 'bg-red-50/40' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-mono text-gray-500 text-xs">#{d.id}</td>

                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">
                        {d.user_username || 'Sin usuario'}
                      </div>
                      <div className="text-xs text-gray-400">
                        user_id: {d.user_id ?? '—'}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{d.recipient_name}</div>
                      <div className="text-xs text-gray-400">{d.recipient_phone}</div>
                    </td>

                    <td className="px-4 py-3 max-w-[220px]">
                      <div className="text-xs text-gray-600 truncate">{d.pickup_address}</div>
                      <div className="text-xs text-gray-400 truncate">→ {d.dropoff_address}</div>
                    </td>

                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-xs">
                        {d.vehicle_type === 'bike' ? (
                          <>
                            <Bike size={13} />
                            Moto
                          </>
                        ) : (
                          <>
                            <Car size={13} />
                            Auto
                          </>
                        )}
                      </span>
                      <div className="text-xs text-gray-400">{d.distance_km} km</div>
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {STATUS_LABELS[d.status] || d.status}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      {d.driver ? (
                        <span className="text-gray-700 text-sm">{d.driver.name}</span>
                      ) : (
                        <span className="text-red-400 text-xs font-medium">Sin asignar</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-right font-semibold text-gray-800">
                      S/ {Number(d.total_price || 0).toFixed(2)}
                    </td>

                    <td className="px-4 py-3 text-right text-brand-primary font-medium text-xs">
                      S/ {Number(d.fastmax_earnings || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedId && (
        <ExpressDetailModal
          deliveryId={selectedId}
          drivers={drivers}
          user_id={user_id}
          onClose={() => setSelectedId(null)}
          onUpdate={updateDelivery}
        />
      )}
    </div>
  );
}